import * as React from 'react';
import classify from '../../utils/classify';
import {TRM_PATHS} from 'src/utils/app-shell';
import {Link} from 'src/rerouter';
import type {MenuOption} from '@spaced-out/ui-design-system/lib/components/Menu/Menu';
import {generateTestId} from '@spaced-out/ui-design-system/lib/utils/qa';
import {Icon} from '@spaced-out/ui-design-system/lib/components/Icon';
import {TruncatedTextWithTooltip} from '@spaced-out/ui-design-system/lib/components/TruncatedTextWithTooltip';
import {
  BodyMedium,
  TEXT_COLORS,
  FormLabelSmall,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {SideMenuLink} from '@spaced-out/ui-design-system/lib/components/SideMenuLink';
import {Badge} from '@spaced-out/ui-design-system/lib/components/Badge';
import {size100} from '@spaced-out/ui-design-system/lib/styles/variables/_size';
import {SideMenuDropdown} from 'src/components/app-shell/side-menu-dropdown';
import {NewBadge} from 'src/components/app-shell/side-menu-new-badge';
import {BetaBadge} from 'src/components/app-shell/side-menu-beta-badge';
import css from './side-menu-group.css';


type BadgeTextDetails = {
  [key: string]: string;
};

export type SideMenuGroupProps = {
  sideMenuLinkKey: string;
  expanded: boolean;
  customTitle: string;
  children?: React.ReactNode;
  selectedMenuKey?: string;
  selectedValue: string;
  onSubLinkSelect: (key: string) => void;
  subLinkOptions: Array<MenuOption>;
  getPathFromPageNameKey: (key: string) => string;
  showStatusIndicator?: boolean;
  badgeTextDetails?: BadgeTextDetails;
};

export const SideMenuGroup = ({
  sideMenuLinkKey = 'dashboard',
  expanded,
  onSubLinkSelect,
  subLinkOptions,
  customTitle,
  selectedValue,
  getPathFromPageNameKey,
  showStatusIndicator,
  badgeTextDetails = {},
}: SideMenuGroupProps) => {
  const [isCollapsed, setIsCollapsed] = React.useState(false); // If initially all open, then false.
  const handleMenuOptionSelect = (option: MenuOption) => {
    onSubLinkSelect(option.key);
  };

  const onChange = () => setIsCollapsed(!isCollapsed);

  const isChildSelected = React.useMemo(() => {
    subLinkOptions;
    for (const item of subLinkOptions) {
      if (item.key === selectedValue) {
        return true;
      }
    }
    return false;
  }, [subLinkOptions, selectedValue]);

  return (
    <>
      {expanded ? (
        <div
          className={css.subMenuGroupWrapper}
          key={`${sideMenuLinkKey}-group`}
          data-test-id={`${sideMenuLinkKey}-side-menu-group`}
        >
          <SideMenuLink
            opened={true}
            pageNameKey={sideMenuLinkKey}
            customTitle={customTitle}
            classNames={{wrapper: css.subMenuGroupLink}}
            isGroupMenuLink={true}
            onChange={onChange}
            rightSlot={
              <div className={css.rightSlotContainer}>
                <Icon
                  name={isCollapsed ? 'chevron-right' : 'chevron-down'}
                  size="small"
                  type="solid"
                  color={TEXT_COLORS.inverseSecondary}
                />
              </div>
            }
            status={
              isCollapsed && showStatusIndicator ? 'information' : undefined
            }
            testId={`${sideMenuLinkKey}-side-menu-link`}
          />
          {!isCollapsed ? (
            <div className={css.subMenuGroup}>
              {subLinkOptions.map((option) => {
                const link = Object.keys(TRM_PATHS).includes(option.key)
                  ? // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly 'jobs.onboarding': "/trm#/onboarding"; readonly 'jobs.hiringplan': "/trm#/settings/hiring-plan-setup/hiring-plans"; readonly 'people.trm-candidates': "/trm#/candidates"; ... 6 more ...; readonly 'jobs.trm': "/trm#/jobs"; }'.
                    TRM_PATHS[option.key]
                  : getPathFromPageNameKey(option.key);

                return (
                  <SideMenuSubLink
                    key={option.key}
                    pageNameKey={option.key}
                    pageNameLabel={option.label}
                    selectedValue={selectedValue}
                    onSubLinkSelect={onSubLinkSelect}
                    to={link}
                    badgeText={badgeTextDetails[option.key]}
                    iconRight={option.iconRight}
                  />
                );
              })}
            </div>
          ) : null}
        </div>
      ) : (
        <SideMenuDropdown
          key={`${sideMenuLinkKey}-group`}
          testId={sideMenuLinkKey}
          anchorPosition="right"
          menu={{
            isFluid: false,
            menuDisabled: false,
            selectedKeys: [selectedValue],
            size: 'small',
            width: size100,
            classNames: {
              wrapper: css.menuWrapper,
            },
            groupTitleOptions: [
              {
                groupTitle: (
                  <FormLabelSmall color="tertiary">
                    {customTitle}
                  </FormLabelSmall>
                ),
                options: subLinkOptions,
              },
            ],
          }}
          onOptionSelect={handleMenuOptionSelect}
          sideMenuLink={{
            opened: false,
            pageNameKey: sideMenuLinkKey,
            tabIndex: 0,
            selectedValue: isChildSelected ? sideMenuLinkKey : '',
          }}
          classNames={{dropdownContainer: css.dropdownWrapper}}
          showStatusIndicator={showStatusIndicator}
          badgeTextDetails={badgeTextDetails}
        />
      )}
    </>
  );
};

export type SideMenuSubLinkProps = {
  pageNameKey: string;
  pageNameLabel?: string;
  selectedValue?: string;
  disabled?: boolean;
  onSubLinkSelect: (key: string) => void;
  to: string;
  badgeText?: string;
  badgeTextDetails?: BadgeTextDetails;
  iconRight?: string;
};

export const SideMenuSubLink = ({
  pageNameKey,
  pageNameLabel,
  selectedValue,
  disabled = false,
  onSubLinkSelect,
  to,
  badgeText = '',
  iconRight = '',
}: SideMenuSubLinkProps) => {
  const selected = selectedValue === pageNameKey;
  const handleClick = () => {
    onSubLinkSelect(pageNameKey);
  };
  return (
    <Link
      to={to}
      key={pageNameKey}
      // @ts-expect-error - TS2322 - Type '{ children: Element; to: string; key: string; tabIndex: number; className: string; onClick: (e: MouseEvent<HTMLElement, MouseEvent>) => void; }' is not assignable to type 'IntrinsicAttributes & { to?: LocationType | ((arg1: RerouterLocation) => LocationType) | null | undefined; ... 6 more ...; handleExtension?: boolean | undefined; }'.
      tabIndex={disabled ? -1 : 0}
      className={css.link}
      onClick={(e) => {
        e.preventDefault();
        handleClick();
      }}
      testId={`${pageNameKey}-side-menu-sub-link`}
    >
      <div
        className={classify(css.subLinkWrapper, {
          [css.selected]: selected,
          [css.disabled]: disabled,
        })}
        tabIndex={-1}
        key={pageNameKey}
        data-testid={generateTestId({
          base: 'side-menu-sub-link',
          index: pageNameKey,
        })}
      >
        <BodyMedium className={css.menuText}>
          <TruncatedTextWithTooltip
            children={pageNameLabel}
            tooltip={{
              placement: 'right',
              classNames: {tooltip: css.toolTip},
            }}
          />
        </BodyMedium>
        {badgeText && (
          <Badge
            fill="blueDark"
            text={badgeText}
            classNames={{wrapper: css.menuBadge}}
          />
        )}
        {iconRight && (
          <Icon
            name={iconRight}
            size="small"
            type="solid"
            className={css.subMenuLinkRightIcon}
            color={TEXT_COLORS.inversePrimary}
          />
        )}
        {pageNameKey === 'agents' && <NewBadge />}
        {pageNameKey === 'byod' && <BetaBadge />}
      </div>
    </Link>
  );
};
